import * as React from 'react';
import { Text } from '@/hen-ui/atomics/Text';
import { Button } from '@/hen-ui/atomics/Button';
import { Flex } from '@/hen-ui/composites/Flex';
import type { TextCellValue } from '../../types';
import { cn } from '@/hen-ui/utils';

interface TextCellProps {
  value: TextCellValue | undefined;
}
export const TextCell: React.FC<TextCellProps> = ({ value }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [needsClamp, setNeedsClamp] = React.useState(false);
  const textRef = React.useRef<HTMLElement>(null);
  // Ref mirrors isExpanded so checkOverflow can read the current value
  const isExpandedRef = React.useRef(false);

  const checkOverflow = React.useCallback(() => {
    // Skip measurement while expanded — the text element has no overflow when
    // line-clamp-3 is removed, which would incorrectly hide the "Read less" button.
    if (textRef.current && !isExpandedRef.current) {
      setNeedsClamp(
        textRef.current.scrollHeight > textRef.current.clientHeight,
      );
    }
  }, []);

  const handleToggle = React.useCallback(() => {
    const next = !isExpandedRef.current;
    isExpandedRef.current = next;
    setIsExpanded(next);
  }, []);

  React.useEffect(() => {
    isExpandedRef.current = false;
    setIsExpanded(false);
    checkOverflow();
  }, [value, checkOverflow]);

  React.useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow]);

  if (!value?.text) {
    console.error(
      '[TextCell] No text provided. TextCell requires a text value.',
    );
    return null;
  }

  return (
    <Flex direction='column' gap='xxs' align='start'>
      <Text
        ref={textRef}
        tag='span'
        variant='b5'
        color='subtext'
        className={cn(!isExpanded && 'line-clamp-3')}
      >
        {value.text}
      </Text>
      {needsClamp && (
        <Button
          type='button'
          variant='textOnlyNeutral'
          onClick={handleToggle}
          className='underline decoration-solid h-auto font-bold text-l1 leading-l1'
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </Flex>
  );
};

TextCell.displayName = 'TextCell';
